import React from 'react'
import * as yup from 'yup'
import { Grid, Icon } from '@softwaredev_cegeka/stix-core'
import {
    Fieldset,
    FormGroup,
    Input,
    Textarea,
} from '@softwaredev_cegeka/stix-form'
import { Select } from '@softwaredev_cegeka/stix-form-select'

import BaseForm from 'src/components/Forms/BaseForm'
import AsyncSelect from 'src/components/Forms/AsyncSelect'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import { AuthApi } from 'src/api/authApi'
import { Link } from 'react-router-dom'

import './serviceForm.scss'

class ServiceActionForm extends BaseForm {
    pages = [
        {
            renderMethod: this.renderForm,
            validationSchema: yup.object().shape({
                serviceName: yup.string(),
                category: yup
                    .object()
                    .shape({
                        isNewOption: yup.bool(),
                        value: yup
                            .string()
                            .matches(/^[a-zA-Z0-9_ ]+$/, {
                                message:
                                    'Please insert alphanumeric characters only',
                                excludeEmptyString: true,
                            })
                            .max(100, 'Please insert less than 100 characters')
                            .required(
                                'Please insert the category of the service action'
                            ),
                        label: yup.string(),
                    })
                    .typeError(
                        'Please insert the category of the service action'
                    ),
                key: yup
                    .string()
                    .matches(/^[a-zA-Z0-9_ ]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .max(100, 'Please insert less than 100 characters')
                    .required('Please insert the key of the service action'),
                description: yup
                    .string()
                    .matches(/^[a-zA-Z0-9 ]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .required('Please add a short description'),
                accessLevel: yup
                    .object()
                    .required('Please choose the access level'),
            }),
        },
    ]

    initialValues = {
        serviceName: '',
        category: '',
        key: '',
        description: '',
        accessLevel: '',
    }

    accessLevels = {
        basic: { value: 'basic', label: 'Basic', id: 1 },
        power_user: { value: 'power_user', label: 'Power user', id: 2 },
        global_admin: { value: 'global_admin', label: 'Global admin', id: 3 },
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    constructor(props, context) {
        super(props, context)
        this.state.values = { ...this.initialValues }
        notifierStore.setShowMessage(false)
        this.authClient.fetchService(props.match.params.id).then(response => {
            this.setState({
                values: {
                    ...this.state.values,
                    serviceName: response?.data?.name,
                },
            })
        })
        if (props.match.params.actionId) {
            this.authClient
                .fetchServiceAction(
                    this.props.match.params.id,
                    this.props.match.params.actionId
                )
                .then(response => {
                    this.setState({
                        values: {
                            ...this.state.values,
                            category: response?.data?.category,
                            key: response?.data?.key,
                            description: response?.data?.description,
                            accessLevel: response?.data?.access_level,
                        },
                    })
                })
        }
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    generatePayload = () => {
        const values = this.state.values
        return {
            category: values.category.value,
            key: values.key,
            description: values.description,
            access_level: values.accessLevel?.value,
        }
    }

    getSubmitUrl() {
        if (this.props.match.params.actionId) {
            return `/api/auth/services/${this.props.match.params.id}/actions/${this.props.match.params.actionId}`
        }
        return `/api/auth/services/${this.props.match.params.id}/actions`
    }

    getSubmitMethod() {
        if (this.props.match.params.actionId) {
            return 'PUT'
        }
        return 'POST'
    }

    getSuccessMessage() {
        const message = `Service action successfully ${
            this.props.match.params.actionId ? 'updated' : 'created'
        }`
        this.props.history.push(`/service-form/${this.props.match.params.id}`)
        return message
    }

    getFailureMessage() {
        if (this.props.match.params.actionId) {
            return 'Service action could not be modified'
        }
        return 'Service action could not be created'
    }

    getNewOptionData(inputValue, optionLabel) {
        return {
            isNewOption: true,
            value: inputValue,
            label: optionLabel,
        }
    }

    formatCreateLabel(inputValue) {
        return `${inputValue} (new)`
    }

    handleCategoryRender = response => {
        return response.data.results.map((item, index) => ({
            id: index,
            value: item,
            label: item,
        }))
    }

    renderForm() {
        return (
            <>
                <div className={'header-form-wrapper'}>
                    <Link
                        to={{
                            pathname:
                                '/service-form/' + this.props.match.params.id,
                        }}
                        className="back-btn"
                    >
                        <Icon icon="back" />
                        <span>Back</span>
                    </Link>
                </div>
                <Fieldset title="Service info">
                    <Grid.Row>
                        <Grid.Col lg={6}>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('serviceName')}
                                    label="Service Name"
                                    disabled
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                </Fieldset>
                <Fieldset title="General info">
                    <Grid.Row>
                        <Grid.Col>
                            <FormGroup>
                                <AsyncSelect
                                    {...this.getCommonSelectProps('category')}
                                    getNewOptionData={this.getNewOptionData}
                                    formatCreateLabel={this.formatCreateLabel}
                                    comboBox={true}
                                    apiConfig={{
                                        url: `/api/auth/services/${this.props.match.params.id}/actions/categories`,
                                        params: inputValue => ({
                                            page: 1,
                                            category_like: inputValue,
                                            sort: '+category',
                                        }),
                                    }}
                                    required
                                    itemRenderer={this.handleCategoryRender}
                                    label="Category"
                                    search
                                />
                            </FormGroup>
                        </Grid.Col>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('key')}
                                    label="Key"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                </Fieldset>
                <Grid.Row>
                    <Grid.Col>
                        <FormGroup>
                            <Textarea
                                {...this.getCommonInputProps('description')}
                                label="Description"
                                required
                            />
                        </FormGroup>
                    </Grid.Col>
                    <Grid.Col>
                        <FormGroup>
                            <Select
                                {...this.getCommonInputProps('accessLevel')}
                                label="Access level"
                                options={[...Object.values(this.accessLevels)]}
                                value={
                                    this.accessLevels[
                                        this.state.values.accessLevel ?? 'basic'
                                    ]
                                }
                                isSearchable
                                required
                                onChange={accessLevel =>
                                    this.handleChange({
                                        target: {
                                            value: accessLevel,
                                            name: 'accessLevel',
                                        },
                                    })
                                }
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
            </>
        )
    }
}

export default ServiceActionForm
